import { HomeSocialProof } from './HomeSocialProof';
import { HomeThemeGallery } from './HomeThemeGallery';
import './HomeHero.css';
import './HomePage.css';
import { useNavigate } from 'react-router-dom';
import { useAnnualParticipantCount } from '@src/state/useAnnualParticipantCount';
import { DigitCounter } from '@src/components/DigitCounter/DigitCounter';
import { HomeArtwork } from '@src/components/HomeArtwork/HomeArtwork';
import { Trans, useTranslation } from 'react-i18next';
import { useScrollPastHeaderAnchor } from '@src/components/layout/useScrollPastHeaderAnchor';
import {
  Button,
  Columns,
  Container,
  Expandable,
  ExpandableGroup,
  ExchangeCard,
  HomeTitle,
  PreviewCard,
  Link,
  Shell,
  Stack,
  Typography,
} from '@src/components';
import { EXCHANGE_CARDS } from '@src/content/exchanges';

import { isDotCom } from '@src/site';
import {
  LandingActions,
  LandingContent,
  LandingSection,
  LandingMoreLink,
  ExchangeGrid,
} from '@src/components/LandingLayout/LandingLayout';

const FAQ_ITEMS = [
  { id: 'whatIs' },
  { id: 'howToPlay' },
  { id: 'money' },
  { id: 'box' },
  { id: 'organizer' },
  { id: 'limit' },
  { id: 'needRegistration', ruOnly: true },
  { id: 'quickWithoutBox', comOnly: true },
  { id: 'why' },
] as const;

const STEPS = [
  {
    step: 1,
    tone: 'support' as const,
    title: 'home.steps.one.title',
    body: 'home.steps.one.body',
  },
  {
    step: 2,
    tone: 'accent' as const,
    title: 'home.steps.two.title',
    body: 'home.steps.two.body',
  },
  {
    step: 3,
    tone: 'info' as const,
    title: 'home.steps.three.title',
    body: 'home.steps.three.body',
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pastCreate = useScrollPastHeaderAnchor('home-create');
  const com = isDotCom();
  const participantCount = useAnnualParticipantCount();
  // Fixed .com display baseline plus the actual annual participation total.
  const displayedParticipantCount =
    participantCount === null
      ? null
      : com
        ? 100_000 + participantCount
        : participantCount;

  const questions = FAQ_ITEMS.filter((item) => {
    if ('ruOnly' in item && item.ruOnly) {
      return !com;
    }
    if ('comOnly' in item && item.comOnly) {
      return com;
    }
    return true;
  });

  return (
    <Shell
      stickyDesktopHeader
      landscape="clear"
      landscapeForeground={
        <DigitCounter
          value={displayedParticipantCount}
          label={t('home.counterLabel')}
          locale="en-US"
          testId="home-counter"
        />
      }
      headerCta={
        pastCreate ? (
          <Button
            variant="primary"
            size="small"
            testId="header-create"
            onClick={() => navigate('/box/new')}
          >
            {t('home.create')}
          </Button>
        ) : null
      }
    >
      <LandingContent testId="home-main">
        <LandingSection variant="hero" gap={6} align="center" testId="home-intro">
          {com ? (
            <Container width="measure">
              <Typography
                as="h1"
                role="title/landing"
                className="home-title home-title--generator"
                testId="home-title"
              >
                <span>
                  <span className="home-title__intro">{t('home.heroTitleIntro')}{' '}</span>
                  <span className="home-title__product">
                    {t('home.heroTitleProduct')}
                    <span className="home-title__dot">.</span>
                  </span>
                </span>
              </Typography>
            </Container>
          ) : (
            <HomeTitle name={t('brand.name')} testId="home-title" />
          )}
          <LandingActions>
            <Button
              variant="primary"
              size="large"
              testId="home-create"
              onClick={() => navigate('/box/new')}
            >
              {t('home.create')}
            </Button>
            <Link href="/randomizer" variant="plain" testId="home-hero-quick-draw">
              {t('home.quickDrawAlt')}
            </Link>
          </LandingActions>
          <HomeThemeGallery />
        </LandingSection>

        <LandingSection
          variant="ground"
          gap={6}
          align="center"
          wide
          continueLandscape
          testId="home-overview"
        >
          <Stack gap={4} align="center" wide>
            <Typography as="h2" role="title/section">
              {t('home.steps.title')}
            </Typography>
            <Container width="measure">
              <Typography as="p" role="body/lead">
                {t('home.lead')}
              </Typography>
            </Container>
            <Typography as="p" role="meta/helper" testId="home-trust-line">
              {t('home.trustLine')}
            </Typography>
          </Stack>
          <Columns columns="fill" gap={5} className="home-steps">
            {STEPS.map((item) => (
              <PreviewCard
                key={item.step}
                tone={item.tone}
                step={item.step}
                title={t(item.title)}
                testId={`home-step-${item.step}`}
              >
                {t(item.body)}
              </PreviewCard>
            ))}
          </Columns>
        </LandingSection>

        <HomeSocialProof />

        <LandingSection variant="ground" gap={6} align="center" wide testId="home-exchanges">
          <Stack gap={3} align="center">
            <Typography as="h2" role="title/section">
              {t('home.exchanges.title')}
            </Typography>
            <Typography as="p" role="body/lead">
              {t('home.exchanges.lead')}
            </Typography>
          </Stack>
          <ExchangeGrid>
            {EXCHANGE_CARDS.map((card) => (
              <ExchangeCard
                key={card.href}
                href={card.href}
                image={card.image}
                icon={<HomeArtwork name={card.icon} />}
                detailsLabel={t('home.exchanges.details', {
                  defaultValue: com ? 'Learn more' : 'Подробнее',
                })}
                tag={t(card.tagKey)}
                title={t(card.titleKey)}
                testId={`home-exchange-${card.href.split('/').pop()}`}
              />
            ))}
          </ExchangeGrid>
        </LandingSection>

        <LandingSection variant="faq" gap={8} wide testId="home-faq">
          <Stack gap={3} align="center">
            <Typography as="h2" role="title/section">
              {t('home.faq.title')}
            </Typography>
          </Stack>
          <ExpandableGroup testId="home-faq-list">
            {questions.map((item) => (
              <Expandable icon={false} key={item.id} title={t(`home.faq.${item.id}.q`)}>
                <Trans
                  i18nKey={`home.faq.${item.id}.a`}
                  components={{
                    br: <br />,
                    b: <Typography as="span" role="body/strong" />,
                    randomizer: <Link href="/randomizer" variant="inline" />,
                  }}
                />
              </Expandable>
            ))}
          </ExpandableGroup>
          <LandingMoreLink>
            <Link href="/faq" testId="home-faq-more">
              {t('home.faq.moreButton')} →
            </Link>
          </LandingMoreLink>
        </LandingSection>
        <LandingSection variant="cta" align="center" testId="home-final-cta">
          <Typography as="h2" role="title/section">
            {t('home.title')}
          </Typography>
          <Button
            variant="primary"
            size="large"
            onClick={() => navigate('/box/new')}
            testId="home-final-create"
          >
            {t('home.create')}
          </Button>
          <Typography as="p" role="body/sm">
            {t('home.trustLine')}
          </Typography>
        </LandingSection>
      </LandingContent>
    </Shell>
  );
}
